import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Chart from 'react-apexcharts';
import { Button, ButtonGroup, Col, Form, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { getUtilizationReportAPI } from '../../../Services/mockApi';
import PageHeader from '../../../Components/shared/PageHeader';
import LoadingSkeleton from '../../../Components/shared/LoadingSkeleton';
import DataTableWrapper from '../../../Components/shared/DataTableWrapper';
import { companyUrl } from '../../../utils/mhNav';

export default function UtilizationReport() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);
  const [groupBy, setGroupBy] = useState('department');
  const [range, setRange] = useState({ from: '', to: '' });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getUtilizationReportAPI(range, groupBy);
      setPayload(res.data);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [range, groupBy]);

  useEffect(() => {
    load();
  }, [load]);

  const barOptions = useMemo(() => {
    const rows = payload?.rows || [];
    return {
      chart: { toolbar: { show: false } },
      xaxis: { categories: rows.map((r) => r.dimension) },
      plotOptions: { bar: { borderRadius: 4, horizontal: false } },
      dataLabels: { enabled: false },
    };
  }, [payload]);

  const barSeries = useMemo(() => {
    const rows = payload?.rows || [];
    return [{ name: 'Meetings', data: rows.map((r) => r.meetingCount) }];
  }, [payload]);

  const donutOptions = useMemo(
    () => ({
      labels: (payload?.donut || []).map((d) => d.label),
      legend: { position: 'bottom' },
    }),
    [payload]
  );

  const donutSeries = useMemo(() => (payload?.donut || []).map((d) => d.value), [payload]);

  const columns = useMemo(
    () => [
      { name: 'Dimension', selector: (r) => r.dimension, grow: 2 },
      { name: 'Meetings', selector: (r) => r.meetingCount },
      { name: '% Share', selector: (r) => `${r.pctShare}%` },
      { name: 'Avg duration (min)', selector: (r) => r.avgDurationMin },
      { name: 'Cancel rate %', selector: (r) => r.cancellationRate },
    ],
    []
  );

  if (error && !payload) {
    return (
      <div>
        <PageHeader title="Utilization" breadcrumbs={[{ label: 'Company', path: companyUrl('dashboard') }, { label: 'Reports', path: companyUrl('reports') }, { label: 'Utilization' }]} />
        <Button onClick={load}>Retry</Button>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Utilization report"
        breadcrumbs={[
          { label: 'Company', path: companyUrl('dashboard') },
          { label: 'Reports', path: companyUrl('reports') },
          { label: 'Utilization' },
        ]}
        secondaryActions={[{ label: 'Export', onClick: () => toast.success('Export (mock)') }]}
      />
      {loading && !payload ? (
        <LoadingSkeleton rows={4} cols={3} />
      ) : (
        <>
          <div className="d-flex flex-wrap gap-2 mb-3">
            <Form.Control type="date" size="sm" style={{ width: 160 }} value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} />
            <Form.Control type="date" size="sm" style={{ width: 160 }} value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} />
            <ButtonGroup size="sm">
              <Button variant={groupBy === 'department' ? 'primary' : 'outline-primary'} onClick={() => setGroupBy('department')}>
                By Department
              </Button>
              <Button variant={groupBy === 'organizer' ? 'primary' : 'outline-primary'} onClick={() => setGroupBy('organizer')}>
                By Organizer
              </Button>
              <Button variant={groupBy === 'provider' ? 'primary' : 'outline-primary'} onClick={() => setGroupBy('provider')}>
                By Provider
              </Button>
            </ButtonGroup>
          </div>
          <Row className="g-3 mb-3">
            <Col lg={8}>
              <div className="border rounded p-2 bg-white">
                <Chart options={barOptions} series={barSeries} type="bar" height={320} />
              </div>
            </Col>
            <Col lg={4}>
              <div className="border rounded p-2 bg-white">
                <Chart options={donutOptions} series={donutSeries} type="donut" height={320} />
              </div>
            </Col>
          </Row>
          <DataTableWrapper columns={columns} data={payload?.rows || []} loading={false} showSearch={false} />
        </>
      )}
    </div>
  );
}
